if (!customElements.get('card-mobile-gallery')) {
  customElements.define('card-mobile-gallery', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.mobile = window.matchMedia('(max-width: 749px)');
      this.mobile.addEventListener('change', () => this.activate(), { signal });
      this.activate();
    }

    activate() {
      if (!this.mobile.matches || this.hasAttribute('ready')) return;
      const template = this.querySelector('template');
      if (!template) return;
      this.append(template.content.cloneNode(true));
      this.track = this.querySelector('.card-mobile-gallery__track');
      const indicator = this.querySelector('.card-mobile-gallery__indicator');
      this.indicators = Array.from(this.track.children, () => {
        const mark = document.createElement('span');
        indicator.append(mark);
        return mark;
      });
      const visibleCount = Math.min(5, this.indicators.length);
      const dotStep = 16;
      indicator.style.width = `${(visibleCount - 1) * dotStep + 6}px`;
      const { signal } = this.controller;
      let windowStart = 0;
      const update = (recenter = false) => {
        const count = this.indicators.length;
        const index = Math.max(0, Math.min(count - 1, Math.round(this.track.scrollLeft / this.track.clientWidth) || 0));
        // Let the selected dot advance before sliding the window after a swipe.
        if (recenter) windowStart = Math.max(0, Math.min(index - 2, count - visibleCount));
        else if (index < windowStart || index >= windowStart + visibleCount) {
          windowStart = Math.max(0, Math.min(index - 2, count - visibleCount));
        }
        this.indicators.forEach((mark, position) => {
          const slot = position - windowStart;
          mark.style.transform = `translateX(${slot * dotStep}px)`;
          mark.style.opacity = slot >= 0 && slot < visibleCount ? '1' : '0';
          mark.classList.toggle('is-active', position === index);
        });
      };
      update();
      this.track.addEventListener('scroll', () => {
        clearTimeout(this.indicatorTimer);
        update();
        this.indicatorTimer = setTimeout(() => update(true), 160);
      }, { passive: true, signal });
      this.track.addEventListener('pointerdown', (event) => {
        this.start = { x: event.clientX, y: event.clientY };
        this.dragged = false;
      }, { passive: true, signal });
      this.track.addEventListener('pointermove', (event) => {
        if (this.start && Math.hypot(event.clientX - this.start.x, event.clientY - this.start.y) > 8) {
          this.dragged = true;
        }
      }, { passive: true, signal });
      this.track.addEventListener('click', (event) => {
        if (this.dragged) {
          event.preventDefault();
          event.stopPropagation();
        }
      }, { capture: true, signal });
      this.track.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const width = this.track.clientWidth;
        let left = Math.round(this.track.scrollLeft / width) * width;
        if (event.key === 'Home') left = 0;
        else if (event.key === 'End') left = this.track.scrollWidth;
        else left += event.key === 'ArrowRight' ? width : -width;
        this.track.scrollTo({ left, behavior: 'instant' });
      }, { signal });
      this.resizeObserver = new ResizeObserver(() => update(true));
      this.resizeObserver.observe(this.track);
      this.setAttribute('ready', '');
    }

    disconnectedCallback() {
      clearTimeout(this.indicatorTimer);
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      this.querySelector('.card-mobile-gallery__track')?.remove();
      this.querySelector('.card-mobile-gallery__indicator')?.remove();
      this.removeAttribute('ready');
    }
  });

  // Boost's app owns the grid. Enhance its existing image link and product data.
  const mobile = window.matchMedia('(max-width: 749px)');
  let queued = false;
  const refresh = () => {
    queued = false;
    if (!mobile.matches) return;
    document.querySelectorAll('.boost-sd__filter-product-list .boost-sd__product-item').forEach((card) => {
      const link = card.querySelector('a.boost-sd__product-image');
      if (!link || link.querySelector('card-mobile-gallery')) return;
      let product;
      try { product = JSON.parse(card.dataset.product); } catch { return; }
      if (!Array.isArray(product.images) || product.images.length < 2) return;
      const gallery = document.createElement('card-mobile-gallery');
      gallery.style.setProperty('--gallery-count', product.images.length);
      const template = document.createElement('template');
      const track = document.createElement('div');
      track.className = 'card-mobile-gallery__track';
      track.tabIndex = 0;
      track.setAttribute('role', 'group');
      const title = link.getAttribute('aria-label') || product.handle;
      track.setAttribute('aria-label', (window.cardMobileGalleryLabel || '__PRODUCT__').replace('__PRODUCT__', title));
      product.images.forEach((media) => {
        const image = document.createElement('img');
        const url = new URL(media.src, location.origin);
        url.searchParams.set('width', '720');
        image.src = url.href;
        image.srcset = [165, 360, 533, 720].map((width) => {
          url.searchParams.set('width', width);
          return `${url.href} ${width}w`;
        }).join(', ');
        image.sizes = `${Math.ceil(link.getBoundingClientRect().width)}px`;
        image.loading = 'lazy';
        image.width = media.width;
        image.height = media.height;
        image.alt = media.alt || title;
        track.append(image);
      });
      const indicator = document.createElement('span');
      indicator.className = 'card-mobile-gallery__indicator';
      indicator.setAttribute('aria-hidden', 'true');
      template.content.append(track, indicator);
      gallery.append(template);
      link.append(gallery);
    });
  };
  const queueRefresh = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(refresh);
  };
  new MutationObserver(queueRefresh).observe(document.querySelector('main') || document.body, { childList: true, subtree: true });
  mobile.addEventListener('change', queueRefresh);
  queueRefresh();
}
